import React, { useState } from "react";
import { InventoryControlPanel } from "../components/InventoryControlPanel";
import { useLocation } from "react-router-dom";
import { InventoryTable } from "../components/InventoryTable";
import {
  InventoryFormModal,
  InventoryViewModal,
} from "../components/InventoryModals";
import { ManageVersionsView } from "../components/ManageVersionsView";
import { useInventoryQueries } from "../hooks/useInventoryQueries";
import { useInventoryMutations } from "../hooks/useInventoryMutations";
import { useInventoryDataProcessing } from "../hooks/useInventoryDataProcessing";
import { validateInventoryForm } from "../hooks/useInventoryValidation";
import {
  INVENTORY_TABS,
  INITIAL_INVENTORY_FORM,
} from "../../../constants/globalConstants";
import toast from "react-hot-toast";

const TABS = INVENTORY_TABS;

export const InventorySettings = ({ initialTab = "vendor" }) => {
  const location = useLocation();
  const pathParts = location.pathname.split("/").filter(Boolean);
  const serviceType = pathParts.includes("tower") ? "tower" : "Verve";

  // 1. State
  const [activeTab, setActiveTab] = useState(initialTab);
  const [search, setSearch] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [viewingItem, setViewingItem] = useState(null);
  const [form, setForm] = useState({ ...INITIAL_INVENTORY_FORM, serviceType });
  const [errors, setErrors] = useState({});
  //updated Halimunnisa 2026-06-08
  const [filters, setFiltersObj] = useState({});
  const [isExporting, setIsExporting] = useState(false);

  const setFilters = (key, value) => {
    setFiltersObj((prev) => ({ ...prev, [key]: value }));
  };

  // 2. Data Fetching
  const { data, isLoading, dropdowns, isDropdownsLoading } =
    useInventoryQueries(activeTab);

  // 3. Data Processing
  const { filteredData } = useInventoryDataProcessing({
    data,
    activeTab,
    search,
    filters,
  });
//updated Halimunnisa 2026-06-08: Integrated the new useInventoryDataProcessing hook to handle complex filtering and searching logic for inventory data, enhancing the user experience by providing more accurate and relevant results based on user input and selected filters.
  // 4. Mutations
  const { createItem, updateItem, deleteItem, isProcessing } =
    useInventoryMutations(activeTab);

  const handleExport = async () => {
    if (isExporting || !filteredData || filteredData.length === 0) {
      toast.error("No data to export");
      return;
    }

    try {
      setIsExporting(true);
      const { exportToExcel } = await import("../../../utils/exportUtils");

      const exportData = filteredData.map((item) => {
        // Flatten specific objects that might cause issues in excel or just return the item,
        // exportToExcel usually handles objects ok by stringifying, but let's safely flatten common ones
        const flatItem = { ...item };
        delete flatItem._id;
        delete flatItem.__v;
        if (flatItem.category && typeof flatItem.category === "object")
          flatItem.category =
            flatItem.category.categoryName || flatItem.category.title;
        if (flatItem.brand && typeof flatItem.brand === "object")
          flatItem.brand = flatItem.brand.brandName || flatItem.brand.title;
        if (flatItem.subCategory && typeof flatItem.subCategory === "object")
          flatItem.subCategory =
            flatItem.subCategory.subCategoryName || flatItem.subCategory.title;
        return flatItem;
      });

      await exportToExcel(
        exportData,
        `${activeTab}_Master_Export_${new Date().toISOString().split("T")[0]}`,
      );
      toast.success(`${activeTab} data exported successfully`);
    } catch (error) {
      console.error("Export failed", error);
      toast.error("Failed to export data");
    } finally {
      setIsExporting(false);
    }
  };

  // 5. Handlers
  const handleAdd = () => {
    setEditingItem(null);
    setForm({ ...INITIAL_INVENTORY_FORM, serviceType });
    setErrors({});
    setIsFormOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    const flatten = (val) =>
      typeof val === "object" && val !== null ? val._id : val || "";

    setForm({
      ...INITIAL_INVENTORY_FORM,
      ...item,
      category: flatten(item.category),
      brand: flatten(item.brand),
      units: flatten(item.units),
      vendor: flatten(item.vendor),
      isBatchExpiry: !!(item.hasBatch || item.isBatchExpiry),
      itemName: item.itemName || item.name || "",
      itemCode: item.itemCode || item.code || "",
      isUnique: !!(item.isUnique || item.isSerialManaged),
      uniqueName: item.uniqueName || "",
    });
    setErrors({});
    setIsFormOpen(true);
  };

  const handleSave = async () => {
    const validationErrors = validateInventoryForm(form, activeTab);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      Object.values(validationErrors)
        .slice(0, 2)
        .forEach((msg) => toast.error(msg));
      return;
    }

    const payload = {
      ...form,
      hasBatch: form.isBatchExpiry || form.hasBatch || false,
      uniqueName: form.uniqueName?.trim() || (form.isUnique ? "Serial No" : ""),
    };

    if (activeTab === "item") {
      payload.code = form.itemCode || form.code;
      payload.name = form.itemName || form.name;
      payload.itemName = payload.name;
      payload.itemCode = payload.code;
    }

    try {
      if (editingItem) {
        await updateItem({ id: editingItem._id, data: payload });
      } else {
        await createItem(payload);
      }
      setIsFormOpen(false);
    } catch (err) {
      // Handle field-specific errors from backend (e.g., duplicate vendor code)
      if (err.response?.data?.field) {
        setErrors({ [err.response.data.field]: err.response.data.message });
      } else {
        console.error("Save error:", err);
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this?")) {
      await deleteItem(id);
    }
  };

  return (
    <div className="space-y-5 pb-10">
      <InventoryControlPanel
        activeTab={activeTab}
        setActiveTab={(id) => {
          setActiveTab(id);
          setSearch("");
          setFiltersObj({});
        }}
        search={search}
        setSearch={setSearch}
        filters={filters}
        setFilters={setFilters}
        onAdd={activeTab !== "versions" ? handleAdd : undefined}
        tabs={TABS}
        onExport={handleExport}
        isExporting={isExporting}
      />

      {activeTab === "versions" ? (
        <ManageVersionsView />
      ) : (
        <InventoryTable
          activeTab={activeTab}
          data={filteredData}
          isLoading={isLoading}
          onView={(item) => {
            setViewingItem(item);
            setIsViewOpen(true);
          }}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      <InventoryFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        activeTab={activeTab}
        editingItem={editingItem}
        form={form}
        setForm={setForm}
        errors={errors}
        setErrors={setErrors}
        onSubmit={handleSave}
        dropdowns={dropdowns}
        isSubmitting={isProcessing}
      />

      <InventoryViewModal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        activeTab={activeTab}
        item={viewingItem}
      />
    </div>
  );
};
